import React from "react";
import { toast } from "react-toastify";

import { push } from "connected-react-router";

import * as DatasetAPI from "api/DatasetAPI";

import * as blueprintReducer from "containers/Blueprint/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as runReducer from "containers/Run/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as deploymentsReducer from "containers/Deployments/reducer";
import * as globalReducer from "containers/App/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as wizardReducer from "containers/Wizard/reducer";
import * as tourReducer from "containers/Tour/reducer";
import * as modelGroupReducer from "containers/ModelGroup/reducer";
import * as projectReducer from "containers/Project/reducer";
import * as folderReducer from "containers/Folders/reducer";
import * as reportReducer from "containers/Report/ReportReducer";
import * as nextModelsReducer from "containers/Next/Models/ModelsReducer";
import * as nextReducer from "containers/Next/NextReducer";

import { AibleClientFactory } from "client/aible/factory";
const PROJECT_CLIENT = AibleClientFactory.getInstance().getProjectClient();

import { isEnabled } from "utils/featureFlags";

const UPDATE_FIELD = "tasks/UPDATE_FIELD";
const UPDATE_TASK = "tasks/UPDATE_TASK";

const initialState = {
  tasks: []
};

function tasksReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case UPDATE_TASK:
      return {
        ...state,
        ["tasks"]: { ...state.tasks, [action.id]: { ...action.data } }
      };
    default:
      return state;
  }
}

/* Selectors */
export function getGlobal(state) {
  return state.tasks;
}

export function getTasks(state) {
  return getGlobal(state).tasks || [];
}

export function getClickedTask(state) {
  return getGlobal(state).clickedTask;
}

export function getTasksArr(state) {
  const tasks = getTasks(state);

  return Object.keys(tasks).map((tKey) => tasks[tKey]);
}

export function getTask(state, id) {
  return getTasks(state)?.[id];
}

export function getProjectIdFromTask(state, taskId) {
  const task = getTask(state, taskId);

  return task?.projects?.[0]?.id;
}

export function getSegmentIdFromTask(state, taskId) {
  const task = getTask(state, taskId);

  return task?.projects?.[0]?.segments?.[0]?.id;
}

export function getSplitTasks(state, taskId) {
  const userId = loginReducer.getUserID(state);

  const tasksArr = getTasksArr(state);

  const oTask = getTask(state, taskId);

  if (oTask === undefined) {
    return undefined;
  }

  const projectId = getProjectIdFromTask(state, oTask?.id);
  const project = projectReducer.getProject(state, projectId);

  if (project && project.segments) {
    return project.segments.map((segment) => {
      const fTask = tasksArr.find(
        (task) =>
          getProjectIdFromTask(state, task?.id) === projectId &&
          getSegmentIdFromTask(state, task?.id) === segment?.id &&
          task?.type === oTask?.type &&
          task?.assigned_to?.id === userId
      );

      return { segment: segment, task: { ...fTask } };
    });
  }

  return undefined;
}

export function getSegmentFromTask(state, task) {
  const projectId = getProjectIdFromTask(state, task?.id);
  const project = projectReducer.getProject(state, projectId);

  return project?.segments
    ? project.segments.find((segment) => segment?.id === getSegmentIdFromTask(state, task?.id))
    : undefined;
}

export function getProjectFromTask(state, taskId) {
  const projectId = getProjectIdFromTask(state, taskId);

  return projectId === undefined ? undefined : projectReducer.getProject(state, projectId);
}

/* Actions */
export function updateTasks(tasks) {
  return { type: UPDATE_FIELD, data: tasks, key: "tasks" };
}

export function updateClickedTask(task) {
  return (dispatch, getState) => {
    if (task?.datasetId) {
      dispatch(homeReducer.fetchDataset(task?.datasetId));
    }
    return dispatch({ type: UPDATE_FIELD, data: task, key: "clickedTask" });
  };
}

export function updateTask(task) {
  return { type: UPDATE_TASK, data: task, id: task?.id };
}

export function fetchComments(taskId) {
  return (dispatch, getState) => {
    dispatch(projectReducer.getProjectTaskComments(taskId)).then((comments) => {
      dispatch(
        updateTask({
          ...getTask(getState(), taskId),
          id: taskId,
          comments: comments
        })
      );
    });
  };
}

export function fetchTasks() {
  return (dispatch, getState) => {
    return PROJECT_CLIENT.getProjectTasks().then((response) => {
      let retTasks = {};

      const userId = loginReducer.getUserID(getState());

      response &&
        response
          .filter((task) => task?.assigned_to?.id === userId || task?.created_by?.id === userId)
          .forEach((task) => {
            retTasks[task.id] = { ...task };
          });

      return dispatch(updateTasks(retTasks));
    });
  };
}

export function fetchTask(taskId) {
  return (dispatch, getState) => {
    const task = getTask(getState(), taskId);

    if (task === undefined) {
      setTimeout(() => {
        dispatch(fetchTask(taskId));
      }, 1000);
    } else {
      const nextId = task?.versions?.[0]?.data?.nextId;

      if (nextId !== undefined) {
        dispatch(nextReducer.init(nextId, undefined, true)).then((r) => {
          const datasetId = nextReducer.getDatasetId(getState(), nextId);

          if (datasetId !== undefined) {
            dispatch(homeReducer.fetchDataset(datasetId));
          }
        });
      }

      const projectId = getProjectIdFromTask(getState(), taskId);

      if (projectId !== undefined) {
        const pDatasetId = projectReducer.getDatasetId(getState(), projectId);

        if (pDatasetId !== undefined) {
          dispatch(homeReducer.fetchDataset(pDatasetId));
        }

        dispatch(projectReducer.init(projectId)).then((response) => {
          const taskSegment = getSegmentFromTask(getState(), task);

          const taskRunId = taskSegment?.job?.job_id;
          const taskDrilldown = taskSegment?.conditions?.[0]?.value || "Overall";

          dispatch(projectReducer.updateDrilldownValue(projectId, taskRunId, taskDrilldown));

          const setupObj = task?.versions?.[0]?.data;

          if (setupObj !== undefined) {
            const activeSegment = projectReducer.getActiveSegment(getState(), projectId);

            const runId = activeSegment?.job?.job_id;

            const retSetup = {
              ...setupReducer.getSetup(getState(), runId),
              ...setupObj
            };

            dispatch(setupReducer.updateSetup(runId, retSetup));
            dispatch(projectReducer.resetBudget(projectId));
          }
        });
      }
    }
  };
}

export function saveVersion(task) {
  return (dispatch, getState) => {
    const projectId = getProjectIdFromTask(getState(), task?.id);
    const activeSegment = projectReducer.getActiveSegment(getState(), projectId);

    //Hacky stuff goes here. Should do better converting a node to a task
    const node = {
      leftSection:
        task?.type === "OPTIMIZE_TRANSACTION"
          ? "BASELINE"
          : task?.type === "OPTIMIZE_STRATEGY"
            ? "STRATEGY"
            : task?.type === "OPTIMIZE_COSTS" || task?.type === "OPTIMIZE_BALANCER"
              ? "EXPECTED"
              : task?.type === "FAIR_GUIDE"
                ? "FAIR_GUIDE"
                : task?.type === "OPTIMIZE_BLUEPRINT"
                  ? "BLUEPRINT"
                  : "EXPECTED"
    };

    const setupData = nextModelsReducer.buildSetupStep(getState(), undefined, node, projectId);
    const activeSetupData = setupData[activeSegment?.job?.job_id];

    console.log("save version task setupData", task, setupData, activeSetupData);

    return dispatch(projectReducer.createTaskVersion(task?.id, activeSetupData));

    /*
    export function createSegmentTask(
      projectSegmentId,
      assigned_to,
      type,
      name,
      status = undefined,
      description = undefined
    ) {

      export function createTaskVersion(taskId, data) {
        return (dispatch, getState) => {
          return PROJECT_CLIENT.createProjectTaskVersion(taskId, { data }).then(
            task => getTaskProjects(getState(), task.id).map(
              project => dispatch(fetchProject(project.id))
            )
          );
        }
      }

      */
  };
}

export function openExploreTask(task) {
  return (dispatch, getState) => {
    console.log("open exploiret ask", task);

    dispatch(push("next/" + task?.datasetId + "/" + task?.nextId));

    const fChart = {
      chart: { title: task?.title },
      isTask: true
    };

    // TODO: This should be better, pass chart better and next init shouldnt wipe out selected chart if exists
    setTimeout(() => {
      dispatch(nextReducer.updateSelectedChart(task?.nextId, fChart, fChart));
    }, 2000);
  };
}

export function init(taskId) {
  return (dispatch, getState) => {
    dispatch(fetchTask(taskId));
  };
}

export default tasksReducer;
